import { useParams, Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import Navbar from "../home/components/Navbar";
import FooterSection from "../home/components/FooterSection";
import VenueGallery from "./components/VenueGallery";
import VenueInfoBar from "./components/VenueInfoBar";
import VenueCampaignBanner from "./components/VenueCampaignBanner";
import SimilarVenues from "./components/SimilarVenues";
import VenueCTABar from "./components/VenueCTABar";
import VenueMapSection from "./components/VenueMapSection";
import ReviewSection from "./components/ReviewSection";
import { venueDetails } from "../../mocks/venueDetails";
import { allVenues } from "../../mocks/allVenues";
import { campaigns } from "../../mocks/campaigns";
import { useGeolocation } from "../../hooks/useGeolocation";
import { calcDistance, formatDistance } from "../../utils/distance";

export default function VenuePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const geo = useGeolocation();
  const [realDistanceStr, setRealDistanceStr] = useState<string | null>(null);

  // Auto-request location silently on mount
  useEffect(() => {
    geo.requestLocation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 80);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const detail = venueDetails.find((v) => v.id === id);
  const fallback = allVenues.find((v) => v.id === id);

  // Calculate real distance when GPS is available
  useEffect(() => {
    if (geo.status !== "success" || geo.lat === null || geo.lng === null) return;
    const venueLat = detail?.lat ?? (fallback ? 40.18352 : null);
    const venueLng = detail?.lng ?? (fallback ? 29.06098 : null);
    if (venueLat === null || venueLng === null) return;
    const meters = calcDistance(geo.lat, geo.lng, venueLat, venueLng);
    setRealDistanceStr(formatDistance(meters));
  }, [geo.status, geo.lat, geo.lng, detail, fallback]);

  if (!detail && !fallback) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4">
        <h1 className="text-2xl font-bold text-stone-800">Mekan bulunamadı</h1>
        <button onClick={() => navigate("/")} className="text-emerald-700 hover:underline cursor-pointer">
          Ana sayfaya dön
        </button>
      </div>
    );
  }

  const venue = detail ?? {
    id: fallback!.id,
    name: fallback!.name,
    category: fallback!.category,
    categorySlug: fallback!.categorySlug,
    rating: fallback!.rating,
    reviews: fallback!.reviews,
    distanceKm: fallback!.distanceKm,
    coverImage: fallback!.image,
    gallery: [],
    address: fallback!.address,
    district: fallback!.district,
    phone: fallback!.phone,
    website: "",
    email: "",
    isSponsored: fallback!.isSponsored,
    isEditorPick: fallback!.isEditorPick,
    isOpen: fallback!.isOpen,
    description: fallback!.description,
    longDescription: fallback!.description,
    tags: fallback!.tags,
    lat: 40.18352,
    lng: 29.06098,
    openingHours: [
      { day: "Pazartesi", hours: "09:00 – 18:00", isOpen: true },
      { day: "Salı", hours: "09:00 – 18:00", isOpen: true },
      { day: "Çarşamba", hours: "09:00 – 18:00", isOpen: true },
      { day: "Perşembe", hours: "09:00 – 18:00", isOpen: true },
      { day: "Cuma", hours: "09:00 – 18:00", isOpen: true },
      { day: "Cumartesi", hours: "09:00 – 18:00", isOpen: true },
      { day: "Pazar", hours: "Kapalı", isOpen: false },
    ],
    priceRange: "",
    campaignId: null,
    relatedVenueIds: allVenues
      .filter((v) => v.categorySlug === fallback!.categorySlug && v.id !== fallback!.id)
      .slice(0, 4)
      .map((v) => v.id),
  };

  const campaign = venue.campaignId ? campaigns.find((c) => c.id === venue.campaignId) : null;
  const directionsUrl = `https://www.google.com/maps/dir/?api=1&destination=${venue.lat},${venue.lng}`;

  // Distance display: realtime GPS if available, otherwise static
  const distanceDisplay = realDistanceStr
    ? realDistanceStr
    : `${venue.distanceKm} km`;

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: venue.name, text: venue.address, url: window.location.href });
      } catch {
        // ignore
      }
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Breadcrumb */}
      <div className={`sticky top-0 z-30 transition-all duration-300 ${scrolled ? "bg-white/95 backdrop-blur-md shadow-sm" : "bg-transparent"}`}>
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-3 flex items-center gap-2 text-sm">
          <Link to="/" className="text-stone-500 hover:text-stone-800 transition-colors cursor-pointer whitespace-nowrap">
            Ana Sayfa
          </Link>
          <i className="ri-arrow-right-s-line text-stone-300" />
          <Link
            to={`/category/${venue.categorySlug}`}
            className="text-stone-500 hover:text-stone-800 transition-colors cursor-pointer whitespace-nowrap"
          >
            {venue.category}
          </Link>
          <i className="ri-arrow-right-s-line text-stone-300" />
          <span className="text-stone-800 font-medium truncate">{venue.name}</span>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 md:px-6 pb-24 md:pb-16">
        {/* Gallery */}
        <div className="relative mb-8 mt-2">
          <VenueGallery
            coverImage={venue.coverImage}
            gallery={venue.gallery}
            venueName={venue.name}
          />
        </div>

        {/* Header Row */}
        <div className="flex flex-col md:flex-row md:items-start gap-4 mb-8">
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <span className="px-3 py-1 bg-stone-100 text-stone-600 rounded-full text-xs font-medium">
                {venue.category}
              </span>
              {venue.isEditorPick && (
                <span className="px-3 py-1 bg-amber-100 text-amber-700 rounded-full text-xs font-semibold flex items-center gap-1">
                  <i className="ri-award-line" /> Editör Seçimi
                </span>
              )}
              {venue.isSponsored && (
                <span className="px-3 py-1 bg-stone-200 text-stone-600 rounded-full text-xs font-medium">
                  Sponsorlu
                </span>
              )}
              <span className={`px-3 py-1 rounded-full text-xs font-semibold flex items-center gap-1 ${venue.isOpen ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-600"}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${venue.isOpen ? "bg-emerald-500" : "bg-red-400"}`} />
                {venue.isOpen ? "Şu An Açık" : "Şu An Kapalı"}
              </span>
            </div>

            <h1 className="font-playfair text-3xl md:text-4xl font-bold text-stone-900 mb-3">
              {venue.name}
            </h1>

            <div className="flex flex-wrap items-center gap-4 text-sm text-stone-500">
              <div className="flex items-center gap-1.5">
                {[1,2,3,4,5].map((s) => (
                  <i key={s} className={`ri-star-${venue.rating >= s ? "fill" : venue.rating >= s - 0.5 ? "half-fill" : "line"} text-amber-500 text-base`} />
                ))}
                <span className="font-bold text-stone-800 ml-1">{venue.rating}</span>
                <span className="text-stone-400">({venue.reviews.toLocaleString()} yorum)</span>
              </div>
              <div className="flex items-center gap-1">
                <i className="ri-map-pin-line text-stone-400" />
                <span>{venue.district}, Bursa</span>
              </div>
              <div className="flex items-center gap-1">
                {geo.status === "loading" ? (
                  <>
                    <i className="ri-loader-4-line text-stone-400 animate-spin" />
                    <span className="text-stone-400">Mesafe hesaplanıyor...</span>
                  </>
                ) : (
                  <>
                    <i className={`ri-walk-line ${realDistanceStr ? "text-emerald-600" : "text-stone-400"}`} />
                    <span className={realDistanceStr ? "text-emerald-700 font-medium" : ""}>
                      {distanceDisplay} uzakta
                    </span>
                    {realDistanceStr && (
                      <span className="text-xs text-emerald-500 ml-1 flex items-center gap-0.5">
                        <i className="ri-map-pin-2-line text-xs" />
                        Gerçek mesafe
                      </span>
                    )}
                    <button
                      onClick={() => { setRealDistanceStr(null); geo.requestLocation(); }}
                      title="Konumumu yenile"
                      className="ml-1 w-5 h-5 flex items-center justify-center text-stone-400 hover:text-emerald-600 cursor-pointer transition-colors rounded"
                    >
                      <i className="ri-refresh-line text-xs" />
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>

          <div className="hidden md:flex items-center gap-3 flex-shrink-0">
            <button
              onClick={handleShare}
              className="w-10 h-10 flex items-center justify-center border border-stone-200 rounded-xl text-stone-600 hover:bg-stone-50 transition-colors cursor-pointer"
            >
              <i className="ri-share-line text-lg" />
            </button>
            {venue.phone && (
              <a
                href={`tel:${venue.phone}`}
                className="flex items-center gap-2 border border-stone-200 rounded-xl px-4 py-2.5 text-sm font-medium text-stone-700 hover:bg-stone-50 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-phone-line" />
                {venue.phone}
              </a>
            )}
            <a
              href={directionsUrl}
              target="_blank"
              rel="nofollow noreferrer"
              className="flex items-center gap-2 bg-emerald-700 hover:bg-emerald-800 text-white rounded-xl px-5 py-2.5 text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-navigation-line" />
              Konum Tarifi Al
            </a>
          </div>
        </div>

        {/* Description */}
        <section className="mb-8">
          <h2 className="text-xl font-bold text-stone-800 mb-4">Hakkında</h2>
          <div className="max-w-4xl">
            <p className="text-stone-600 leading-relaxed text-base mb-3">{venue.longDescription}</p>
            {venue.longDescription !== venue.description && (
              <p className="text-stone-500 leading-relaxed text-sm italic">{venue.description}</p>
            )}
          </div>
        </section>

        {campaign && (
          <section className="mb-10">
            <h2 className="text-xl font-bold text-stone-800 mb-4">Aktif Kampanya</h2>
            <VenueCampaignBanner campaign={campaign} />
          </section>
        )}

        <div className="border-t border-stone-100 mb-10" />

        <section className="mb-10">
          <h2 className="text-xl font-bold text-stone-800 mb-5">İletişim & Çalışma Saatleri</h2>
          <VenueInfoBar
            address={venue.address}
            phone={venue.phone}
            website={venue.website}
            email={venue.email}
            lat={venue.lat}
            lng={venue.lng}
            priceRange={venue.priceRange}
            openingHours={venue.openingHours}
            tags={venue.tags}
          />
        </section>

        <VenueMapSection
          venueName={venue.name}
          address={venue.address}
          district={venue.district}
          lat={venue.lat}
          lng={venue.lng}
        />

        <ReviewSection
          venueId={venue.id}
          venueName={venue.name}
          venueLat={venue.lat}
          venueLng={venue.lng}
        />

        <section className="mb-12">
          <SimilarVenues
            relatedVenueIds={venue.relatedVenueIds}
            currentVenueId={venue.id}
          />
        </section>

        <section className="mb-12">
          <h2 className="text-xl font-bold text-stone-800 mb-5">Farklı Kategorilerden Öneriler</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {["food", "nature", "accommodation", "entertainment", "sightseeing", "historical"]
              .filter((slug) => slug !== venue.categorySlug)
              .slice(0, 3)
              .map((slug) => {
                const pick = allVenues.find((v) => v.categorySlug === slug && v.isEditorPick);
                if (!pick) return null;
                return (
                  <article
                    key={pick.id}
                    onClick={() => navigate(`/venue/${pick.id}`)}
                    className="flex gap-4 bg-stone-50 rounded-2xl p-4 cursor-pointer hover:bg-stone-100 transition-colors group"
                  >
                    <div className="w-20 h-20 rounded-xl overflow-hidden flex-shrink-0">
                      <img src={pick.image} alt={pick.name} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300" />
                    </div>
                    <div className="min-w-0">
                      <span className="text-xs text-stone-500 uppercase tracking-wide">{pick.category}</span>
                      <h3 className="font-semibold text-stone-800 text-sm mt-0.5 leading-tight">{pick.name}</h3>
                      <div className="flex items-center gap-1 mt-1.5">
                        <i className="ri-star-fill text-amber-500 text-xs" />
                        <span className="text-xs font-semibold text-stone-700">{pick.rating}</span>
                        <span className="text-stone-300 mx-1">·</span>
                        <span className="text-xs text-stone-500">{pick.district}</span>
                      </div>
                    </div>
                  </article>
                );
              })}
          </div>
        </section>

        <section className="rounded-2xl overflow-hidden relative mb-8">
          <img
            src="https://public.readdy.ai/ai/img_res/7e67edd8d74d19b77468e58a64243c41.jpg"
            alt="Bursa Guide 360"
            className="w-full h-48 object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-stone-900/80 to-stone-900/40 flex items-center px-8">
            <div>
              <h3 className="font-playfair text-white text-2xl font-bold mb-2">Bursa&apos;yı Keşfetmeye Devam Et</h3>
              <p className="text-white/70 text-sm mb-4">500+ mekan, rotalar ve kampanyalar seni bekliyor.</p>
              <div className="flex gap-3">
                <Link
                  to="/"
                  className="bg-white text-stone-800 rounded-lg px-4 py-2.5 text-sm font-semibold hover:bg-stone-100 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Ana Sayfaya Dön
                </Link>
                <Link
                  to={`/category/${venue.categorySlug}`}
                  className="border border-white/40 text-white rounded-lg px-4 py-2.5 text-sm font-medium hover:bg-white/10 transition-colors cursor-pointer whitespace-nowrap"
                >
                  {venue.category}&apos;i Keşfet
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>

      <FooterSection />

      <VenueCTABar
        phone={venue.phone}
        address={venue.address}
        lat={venue.lat}
        lng={venue.lng}
        venueName={venue.name}
      />
    </div>
  );
}
